/**
 * Static Help / FAQ content for DecisionPen.
 * section: "menus" | "product"
 * cta.nav targets: home | home-add | assistant | inbox | approvals |
 *   architecture-gallery | reports | admin | admin:<toolId> | settings | help
 */

export const HELP_SECTIONS = [
  {
    id: "menus",
    label: "Using DecisionPen",
    blurb: "What each menu does and when to use it.",
  },
  {
    id: "product",
    label: "Product & integrations",
    blurb: "SSO, roles, integrations, and how Help relates to Guide and the assistant.",
  },
];

export const HELP_ARTICLES = [
  {
    id: "home",
    section: "menus",
    title: "Home",
    summary:
      "Your starting hub: add knowledge to grow the map, or ask the Architecture assistant about what is already on it.",
    body: [
      "Home has two primary journeys that stay on this screen as panels—you do not leave the hub to start them.",
      "Add knowledge is for notes and documents you want turned into proposed map items. The Architecture assistant answers questions about your landscape and draws diagrams once items exist on the map.",
    ],
    bullets: [
      "Use Add knowledge when you have HLD notes, blueprints, or pasted text to extract.",
      "Use the Architecture assistant for “what connects to X?” and diagrams—not for long product how-tos (those live here in Help).",
      "Shortcuts on Home also jump to Your map, Reports, Admin (if you are an admin), and Settings.",
    ],
    note: "Viewers can open the assistant but cannot Add knowledge or use Inbox.",
    ctas: [
      { label: "Open Home", nav: "home" },
      { label: "Open Add knowledge", nav: "home-add" },
      { label: "Open assistant", nav: "assistant" },
    ],
  },
  {
    id: "inbox",
    section: "menus",
    title: "Inbox",
    summary:
      "Review AI proposals from uploads before anything joins the live map. You stay architect of record.",
    body: [
      "Each upload that extracts architecture becomes a review package. Approve, edit, or discard proposals here. Nothing is published until you say so.",
      "Facets such as Needs your OK, Queued, Processing, Ready, and Failed help you track work.",
    ],
    bullets: [
      "Open Inbox after Add knowledge finishes extracting.",
      "Approve only what you want on the map; discard or fix the rest.",
      "Recent vs archive time ranges help you find older packages.",
    ],
    note: "Contributors and above. Viewers do not see Inbox.",
    ctas: [{ label: "Open Inbox", nav: "inbox" }],
  },
  {
    id: "approvals",
    section: "menus",
    title: "Approvals",
    summary:
      "Baseline approval queues for people in an approver group (L1 / L2).",
    body: [
      "Approvals is separate from Inbox. Inbox is where contributors shape AI proposals; Approvals is where designated reviewers accept or reject items before they become part of the governed baseline.",
    ],
    bullets: [
      "Pending, applied, rejected, and changes-requested states track each submission.",
      "Only users in the approver group see this menu.",
    ],
    note: "Hidden unless your account is in an approver group.",
    ctas: [{ label: "Open Approvals", nav: "approvals" }],
  },
  {
    id: "your-map",
    section: "menus",
    title: "Your map",
    summary:
      "Browse the live enterprise map by domain—business, applications, data, connections, and technology.",
    body: [
      "Your map opens a tile gallery. Opening a tile takes you into Explorer (and the Knowledge Library where relevant) so you can search, open items, add items, and submit for approval.",
    ],
    bullets: [
      "Gallery groups: Business, Applications, Data, Connections, Technology (plus Other when needed).",
      "Explorer is reached from tiles or deep links—not as its own sidebar tab.",
    ],
    ctas: [{ label: "Open Your map", nav: "architecture-gallery" }],
  },
  {
    id: "reports",
    section: "menus",
    title: "Reports",
    summary: "Build, manage, and share custom enterprise-architecture reports.",
    body: [
      "Reports is the catalog of saved and shared EA reports for your organisation. Create views scoped to the parts of the map you care about, then reuse or export them.",
    ],
    ctas: [{ label: "Open Reports", nav: "reports" }],
  },
  {
    id: "admin",
    section: "menus",
    title: "Admin",
    summary:
      "Configure the map model, hierarchies, AI review behaviour, templates, people, and imports.",
    body: [
      "Admin is for administrators. Tools are grouped so you can find the right job quickly.",
    ],
    bullets: [
      "Map setup — item types and custom fields.",
      "Hierarchies — build and import org, objective, initiative, process, and capability trees.",
      "AI map building — match-or-create and review-before-publish policies.",
      "Document templates — blueprint Word/Markdown templates.",
      "People & access — users, roles, and company SSO (Okta).",
      "Import & cleanup — spreadsheet import for catalog items, or clear map data.",
    ],
    note: "Administrators only. Spreadsheet import is for applications, technology, and similar catalog items—not nested org/capability trees (use Hierarchies for those).",
    ctas: [
      { label: "Open Admin", nav: "admin" },
      { label: "Import from spreadsheet", nav: "admin:entity_import" },
      { label: "Build hierarchies", nav: "admin:taxonomy_hierarchies" },
    ],
  },
  {
    id: "settings",
    section: "menus",
    title: "Settings",
    summary: "Personal account settings—change the password you use to sign in.",
    body: [
      "Settings today is focused on changing your password. Organisation-wide configuration lives under Admin.",
    ],
    ctas: [{ label: "Open Settings", nav: "settings" }],
  },
  {
    id: "guide",
    section: "menus",
    title: "Guide (header strip)",
    summary:
      "An optional, interactive walkthrough that helps you add your first knowledge and see it on the map.",
    body: [
      "The Guide is not a sidebar page—it appears as a strip in the header when you start or resume “Show me how…”. It coaches you through a short path (add knowledge → Inbox → map).",
      "Help (this page) is the always-available reference. The Architecture assistant is for landscape questions. Use Guide when you want a hands-on first run; use Help when you need to look something up.",
    ],
    bullets: [
      "Guide = interactive coaching.",
      "Help = menus, features, and product Q&A.",
      "Architecture assistant = your map, diagrams, and landscape Q&A.",
    ],
  },
  {
    id: "add-knowledge",
    section: "menus",
    title: "Add knowledge",
    summary:
      "Paste notes or attach a file on Home so DecisionPen can propose map items for Inbox review.",
    body: [
      "Open Add knowledge from Home. Paste text or attach a document. DecisionPen extracts a proposed map; nothing joins the live map until you approve it in Inbox.",
      "During a trial, Show me how to use DecisionPen can run the same extract path as part of the guided tour.",
    ],
    ctas: [
      { label: "Open Add knowledge", nav: "home-add" },
      { label: "Open Inbox", nav: "inbox" },
    ],
  },
  {
    id: "architecture-assistant",
    section: "menus",
    title: "Architecture assistant",
    summary:
      "Ask about applications, capabilities, integrations, and data on your map; request diagrams and blueprints.",
    body: [
      "Open the assistant from Home. It investigates your architecture repository—confirmed entities and linked evidence—not a general product FAQ.",
      "Example asks: “Explain data architecture of [application]”, “Draw a context diagram for [application]”. Diagrams render in the thread.",
    ],
    note: "For “How do I use DecisionPen?” questions, stay on this Help page instead of the assistant.",
    ctas: [{ label: "Open assistant", nav: "assistant" }],
  },
  {
    id: "sso",
    section: "product",
    title: "Does DecisionPen support SSO?",
    summary:
      "Yes—in the Enterprise version, SSO can be implemented with your company’s identity provider.",
    body: [
      "Enterprise customers can wire DecisionPen to their company IdP (for example Okta, Azure AD / Entra ID, or another SAML/OIDC provider) so people sign in with existing corporate credentials.",
      "Individuals can use Continue with Google for a light trial signup. Company users can Sign in with company SSO (OIDC via Okta or Entra) when their organisation has SSO configured; Google remains available as a fallback for DecisionPen staff.",
    ],
    bullets: [
      "Enterprise: SSO with your company’s identity provider.",
      "Ask your DecisionPen contact to enable and configure IdP SSO for your tenant.",
    ],
  },
  {
    id: "cmdb",
    section: "product",
    title: "Can DecisionPen integrate with a CMDB?",
    summary: "Not yet—CMDB / ServiceNow sync is on the roadmap, not a shipped product feature.",
    body: [
      "DecisionPen today is an enterprise-architecture map and knowledge workspace. You can model applications, technology, and connections, and import catalog items from spreadsheets.",
      "A live bi-directional CMDB or ServiceNow integration is not available in the product yet. Do not assume automatic CI sync. When that capability ships, this Help article will be updated.",
    ],
    bullets: [
      "Near-term: spreadsheet import and document/notes extraction into your map.",
      "Planned: deeper CMDB-style integrations (not available today).",
    ],
  },
  {
    id: "roles",
    section: "product",
    title: "How do roles work?",
    summary:
      "Viewers browse; contributors add and review; administrators configure the map; approvers handle baseline queues.",
    body: [
      "Roles control which menus and actions you see. Typical patterns:",
    ],
    bullets: [
      "Viewer — browse Your map, Reports, and the assistant; no Add knowledge or Inbox.",
      "Contributor — Add knowledge, Inbox review, map edits as allowed.",
      "Administrator — Admin tools (item types, hierarchies, import, users, cleanup).",
      "Approver group — Approvals menu for L1/L2 baseline review (separate from Inbox).",
    ],
    ctas: [{ label: "Users & roles (Admin)", nav: "admin:user_management" }],
  },
  {
    id: "knowledge-flow",
    section: "product",
    title: "Where does uploaded knowledge go before it hits the map?",
    summary:
      "Extracted proposals land in Inbox for your review; only approved items join the live map.",
    body: [
      "Add knowledge (or a guided extract) creates a processing job. When extraction finishes, open Inbox to review the package. Approving publishes to the map according to your organisation’s review policies; discarding keeps the live map unchanged.",
    ],
    ctas: [
      { label: "Open Add knowledge", nav: "home-add" },
      { label: "Open Inbox", nav: "inbox" },
    ],
  },
  {
    id: "guide-vs-help",
    section: "product",
    title: "What’s the difference between Guide, Help, and the assistant?",
    summary: "Three different jobs: coach, reference, and landscape Q&A.",
    body: [
      "Guide (header strip) walks you through a first successful path—usually add knowledge and see results on the map.",
      "Help (this page) explains every menu and answers product questions such as SSO and CMDB.",
      "Architecture assistant (Home) answers questions about your enterprise architecture and produces diagrams from the map.",
    ],
  },
  {
    id: "import-vs-hierarchies",
    section: "product",
    title: "Spreadsheet import vs hierarchy trees?",
    summary:
      "Use Import for catalog items; use Hierarchies for nested org and capability trees.",
    body: [
      "Admin → Import & cleanup → Import from spreadsheet loads applications, technology components, and similar flat catalog items with column mapping.",
      "Organisation, objective, initiative, process, and capability trees use Admin → Hierarchies → Build hierarchies (including that tool’s dedicated import). Do not use the spreadsheet entity import for those trees.",
    ],
    ctas: [
      { label: "Import from spreadsheet", nav: "admin:entity_import" },
      { label: "Build hierarchies", nav: "admin:taxonomy_hierarchies" },
    ],
  },
  {
    id: "tenancy",
    section: "product",
    title: "Is my organisation’s data separate?",
    summary:
      "Yes—each organisation works in its own tenant workspace with its own map and users.",
    body: [
      "DecisionPen isolates organisation data by tenant. Colleagues you invite under your organisation share that map; other organisations cannot see it. Super-user ops tools (when present) are for platform operators, not day-to-day EA work.",
    ],
  },
];

export function filterHelpArticles(query) {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return HELP_ARTICLES;
  return HELP_ARTICLES.filter((article) => {
    const hay = [
      article.title,
      article.summary,
      ...(article.body || []),
      ...(article.bullets || []),
      article.note || "",
    ]
      .join("\n")
      .toLowerCase();
    return hay.includes(q);
  });
}
